import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

export const useCompliance = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/compliance").then((r) => setC(r.data)); }, []);
  return c;
};

const StatePicker = ({ value, onPick, compliance }) => {
  const states = Object.entries(compliance?.states || {});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const shown = open ? q : (compliance?.states?.[value] || q);
  const t = q.trim().toLowerCase();
  const matches = states.filter(([c, n]) => !t || n.toLowerCase().startsWith(t) || c.toLowerCase() === t);
  const pick = (c) => { onPick(c); setQ(""); setOpen(false); };
  return (
    <div className="relative">
      <label htmlFor="state-input" className="text-xs text-slate-400">State of residence</label>
      <input id="state-input" data-testid="state-input" type="text" autoComplete="address-level1" placeholder="Start typing your state…"
        value={shown} onFocus={() => { setQ(""); setOpen(true); }} onClick={() => { if (!open) { setQ(""); setOpen(true); } }} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); onPick(""); }}
        onKeyDown={(e) => { if (e.key === "Enter" && open && matches[0]) { e.preventDefault(); pick(matches[0][0]); } }}
        className="lux-input mt-1" role="combobox" aria-expanded={open} aria-controls="state-list" />
      {open && (
        <ul id="state-list" role="listbox" data-testid="state-list" className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-white/10 bg-[#0F1525] shadow-xl">
          {matches.length === 0 && <li className="px-4 py-3 text-sm text-slate-500">No matching state</li>}
          {matches.map(([c, n]) => (
            <li key={c}>
              <button type="button" data-testid={`state-option-${c}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)}
                className="w-full text-left px-4 py-3 text-sm text-slate-100 hover:bg-white/5 focus:bg-white/5 flex justify-between">
                <span>{n}</span>{compliance.blocked_states.includes(c) && <span className="text-xs text-rose-300">not available yet</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const StateTermsFields = ({ value, onChange, compliance }) => {
  const blocked = compliance?.blocked_states?.includes(value.state);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="space-y-3">
      <StatePicker value={value.state} onPick={(code) => onChange({ ...value, state: code })} compliance={compliance} />
      {blocked && <p data-testid="state-blocked-msg" className="text-sm text-rose-300">Sorry, VoyageQueue isn't available in your state yet.</p>}
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input data-testid="age-checkbox" type="checkbox" checked={value.age_confirmed} onChange={set("age_confirmed")} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
        I am 18 or older and a US resident.
      </label>
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input data-testid="terms-checkbox" type="checkbox" checked={value.accept_terms} onChange={set("accept_terms")} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
        <span>I've read the <Link to="/terms" target="_blank" className="text-[#38BDF8] underline">Terms & Disclosures</Link> and <Link to="/privacy" target="_blank" data-testid="signup-privacy-link" className="text-[#38BDF8] underline">Privacy Policy</Link>. I understand trips are never guaranteed, wait times are estimates, and if sign-ups stop my own payments may be what pays for my trip.</span>
      </label>
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input data-testid="intermediary-checkbox" type="checkbox" checked={!!value.intermediary_ack} onChange={set("intermediary_ack")} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
        <span>I understand VoyageQueue is only an intermediary. It runs the membership and trip fund, while flights, hotels, tours, tickets and other travel services are provided by independent third-party companies under their own terms. VoyageQueue is not the travel provider.</span>
      </label>
    </div>
  );
};

export const complianceReady = (v, c) => v.state && v.age_confirmed && v.accept_terms && v.intermediary_ack && !c?.blocked_states?.includes(v.state);
